import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {DataFrame, DataFrameColumnDef, DataFrameRow} from 'data-frames/DataFrameTypes';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import {createAccessor} from 'data-frames/util/createAccessors';
import getUserColumns from 'data-frames/util/getUserColumns';
import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {readableTypeDef} from 'data-types/benchling-type-def/readableTypeDef';
import SimpleTableEditable from 'notebook/simple-tables/SimpleTableEditable';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {Table} from 'notebook/tables/TableConstants';
import {GRAY_DARK} from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {capitalize} from 'util/NameUtil';

import {
  DEFAULT_COLUMN_WIDTH,
  DISABLED_SIMPLE_TABLE_PROPS,
  LEAF_TYPE_TO_COLUMN_INFO_FN,
  LIST_LEAF_TYPE_TO_COLUMN_INFO_FN,
  MAX_COLUMN_WIDTH,
  COLUMN_WIDTH_PER_HEADER_CHARACTER,
  SUPPORTED_LEAF_TYPES,
  SUPPORTED_LIST_LEAF_TYPES,
} from './DataFrameTableEditableConstants';
import {DataFrameToTableColumnInfo} from './DataFrameTableEditableTypes';

/** @package */
export interface DataFrameTableEditableProps {
  /**
   * The data frame to display in the table.
   *
   * This data frame is used to determine the columns to display in the table and the row delegate constraints.
   * `dataFrameRowsOverride` is used to render the actual row content.
   *
   * We need to separate `originalDataFrame` and `dataFrameRowsOverride` because some updates to
   * `dataFrameRowsOverride` could cause different column constraints. For example, if we have a dropdown
   * field, we use the `originalDataFrame` to set the dropdown options and the `dataFrameRowsOverride` to set
   * the selected dropdown option. Otherwise, we would lose dropdown options when the `dataFrameRowsOverride`
   * changes to have different values.
   */
  originalDataFrame: DataFrame;
  /**
   * The actual rows to display in the table.
   *
   * This can initially be `originalDataFrame.rows`.
   */
  dataFrameRowsOverride: Array<DataFrameRow>;
  clientStateId: string;
  /**
   * Callback for when the user updates the table content.
   *
   * This is called when the underlying table is updated to be different than the `dataFrameRowsOverride`, and
   * never when it hasn't changed. Since that's the only supported operation, the callback is never called in
   * other situations (e.g. can't rename columns, can't add rows, etc.).
   */
  onUpdateDataFrameRows: (dataFrameRowsOverrideOverride: Array<DataFrameRow>) => void;
  disabled?: boolean;
}

const columnWidthsOverrideState = makeUseGlobalClientState<Array<number> | null>(null);

/**
 * Editable version of a UI.
 *
 * This component gives an editing UI to data frames. It only supports editing existing rows, and does not
 * support the following:
 *  - Adding, removing, or editing columns
 *  - Adding or removing rows
 *
 * ### Column Info Initializers
 *
 * Supports all data frame types minimally through treating them like JSON types (the parent type of all
 * benchling types) - it gives a simple dropdown UI for those. For some types, we have overrides to rendering
 * and editing for a better experience - e.g. free form editor for number fields, checkbox for boolean fields,
 * etc.
 *
 * If you want to add more special type support, you need to add a new column initializer in the
 * `/column-info-initializers` directory, and then register it in `DataFrameTableEditableConstants`. This will
 * require setting a new column constraint, type value converter, and width calculation for that type.
 */
export default function DataFrameTableEditable({
  originalDataFrame,
  dataFrameRowsOverride,
  clientStateId,
  onUpdateDataFrameRows,
  disabled = false,
}: DataFrameTableEditableProps): JSX.Element {
  // The underlying `SimpleTableEditable` component assumes there's at least one row to edit.
  assert(
    originalDataFrame.rows.length > 0,
    'DataFrameTableEditable requires at least one row to be editable.'
  );

  const table = useMemo(
    () => convertDataFrameToTable(originalDataFrame, dataFrameRowsOverride, disabled),
    [originalDataFrame, dataFrameRowsOverride, disabled]
  );

  const [columnWidthsOverride, setColumnWidthsOverride] =
    columnWidthsOverrideState.useGlobalClientState(clientStateId);
  const columnWidths = columnWidthsOverride ?? table.widths;

  const handleTableUpdate = useCallback(
    (_index: number, {table: updatedTable}: {table: Table}) => {
      setColumnWidthsOverride(updatedTable.widths);

      const dataFrameRowsOverrideOverride = convertTableToDataFrame(
        updatedTable,
        originalDataFrame,
        dataFrameRowsOverride
      ).rows;

      if (!isEqual(dataFrameRowsOverride, dataFrameRowsOverrideOverride)) {
        onUpdateDataFrameRows(dataFrameRowsOverrideOverride);
      }
    },
    [dataFrameRowsOverride, originalDataFrame, onUpdateDataFrameRows, setColumnWidthsOverride]
  );

  return (
    <div className={`simpleDivTable-row mediocre ${css(dataFrameEditableStyles.tableWrapper)}`}>
      <SimpleTableEditable
        {...DISABLED_SIMPLE_TABLE_PROPS}
        cid={`dataframe-editable-${clientStateId}`}
        table={{
          ...table,
          widths: columnWidths,
        }}
        locked={disabled}
        viewer={disabled}
        updateHandler={handleTableUpdate}
        dataTestAttrs={{
          component: 'DataFrameTableEditable',
          element: 'simple-table-editable',
        }}
      />
    </div>
  );
}

function convertDataFrameToTable(
  originalDataFrame: DataFrame,
  dataFrameRowsOverride: Array<DataFrameRow>,
  disabled: boolean
): Table {
  const {name} = originalDataFrame;
  const columnInfos = getUserColumns(originalDataFrame).map((column) =>
    getDataFrameColumnToTableColumnInfo(column, originalDataFrame, disabled)
  );

  return {
    name,
    columnConstraints: columnInfos.map((info) => info.columnConstraint),
    columnLabels: columnInfos.map((info) => info.columnLabel),
    widths: columnInfos.map((info) => info.width),
    table: dataFrameRowsOverride.map((row) => columnInfos.map((info) => info.dataFrameValueToTableCell(row))),
    lockedDims: {
      allowAddRows: false,
      allowRemoveRows: false,
      allowEditCols: false,
    },
    rowData: originalDataFrame.rows.map((row) => ({
      delegatedColumnConstraints: new Map(
        columnInfos
          .filter(({delegatedColumnDefinitionAccessor}) => delegatedColumnDefinitionAccessor != null)
          .map(({dataFrameColumn, delegatedColumnDefinitionAccessor}) => {
            assert(
              delegatedColumnDefinitionAccessor != null,
              'Null delegatedColumnDefinitionAccessor should have been filtered out.'
            );
            const columnDefinition = delegatedColumnDefinitionAccessor(row);
            return [
              dataFrameColumn.systemName,
              {
                provenanceUuid: dataFrameColumn.systemName,
                columnDefinition,
                structuredTableColumn: {
                  ...makeStructuredTableColumn({
                    columnType: columnDefinition.type,
                    name: dataFrameColumn.displayName,
                    isRequired: columnDefinition.isRequired,
                  }),
                  cid: dataFrameColumn.systemName,
                  provenanceUuid: dataFrameColumn.systemName,
                },
              },
            ];
          })
      ),
    })),
    headerRow: false,
    allowAddRows: false,
    allowRowRemoval: false,
    allowColumnEdits: false,
    maxRows: originalDataFrame.rows.length,
  };
}

function convertTableToDataFrame(
  {columnLabels, table}: Table,
  originalDataFrame: DataFrame,
  dataFrameRowsOverride: Array<DataFrameRow>
): DataFrame {
  const columnInfos = getUserColumns(originalDataFrame).map((column) =>
    getDataFrameColumnToTableColumnInfo(column, originalDataFrame, false)
  );

  // Removing, adding, or editing columns is not supported as of writing this, so if we run into this assertion,
  // it means we didn't block these operations correctly or need to add support for some new feature.
  assert(
    columnLabels.length === columnInfos.length,
    'Expected the columns to match between the table and the data frame'
  );

  return {
    ...originalDataFrame,
    rows: table.map((tableRow, rowIndex) => ({
      // We spread the original row to get hidden columns. This includes the deprecated system columns,
      // and the ones hidden by props.
      ...originalDataFrame.rows[rowIndex],
      // We then spread the dataFrameRowsOverride to get the new values in case there are any.
      ...dataFrameRowsOverride[rowIndex],
      ...Object.fromEntries(
        columnInfos.map((columnInfo, columnIndex) => [
          columnInfo.dataFrameColumn.systemName,
          columnInfo.tableCellToDataFrameValue(tableRow[columnIndex], originalDataFrame.rows[rowIndex]),
        ])
      ),
    })),
  };
}

function getDataFrameColumnToTableColumnInfo(
  column: DataFrameColumnDef,
  dataFrame: DataFrame,
  disabled: boolean
): DataFrameToTableColumnInfo {
  const {
    systemName,
    displayName,
    type: {isList, isNullable, leafType},
  } = column;

  const {accessor: baseAccessor, validatedLeafType} = createAccessor(dataFrame, systemName, {
    allowedLeafTypes: isList ? SUPPORTED_LIST_LEAF_TYPES : SUPPORTED_LEAF_TYPES,
    listType: isList ? ColumnTypeValidationFlag.ALLOWED : ColumnTypeValidationFlag.NOT_ALLOWED,
    nullableType: ColumnTypeValidationFlag.ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
    strictLeafType: false,
  });

  const baseColumnConstraint = {
    renderedLabel: (
      <>
        <BenchlingIcon className={css(dataFrameEditableStyles.typeIcon)} icon={columnToTypeIcon(column)} />{' '}
        {displayName}
      </>
    ),
    tooltip: capitalize(readableTypeDef(leafType)),
    editLabelDisabled: true,
    isProtected: disabled,
    isColumnUserConfigurable: false,
    canResetLabel: false,
    viewer: true,
  };

  const baseStructuredTableColumn = {
    cid: systemName,
    isProtected: disabled,
    isReadOnly: disabled,
    isRequired: !isNullable,
    name: displayName,
    provenanceUuid: systemName,
  };

  const columnInfoInitializer = isList
    ? LIST_LEAF_TYPE_TO_COLUMN_INFO_FN[validatedLeafType]
    : LEAF_TYPE_TO_COLUMN_INFO_FN[validatedLeafType];

  assert(columnInfoInitializer != null, `No column info initializer found for leaf type`, {
    isList,
    isNullable,
    leafType,
    systemName,
    validatedLeafType,
  });

  const baseColumnInfo = columnInfoInitializer(
    column,
    dataFrame,
    baseColumnConstraint,
    baseStructuredTableColumn,
    baseAccessor
  );

  return {
    ...baseColumnInfo,
    columnLabel: displayName,
    dataFrameColumn: column,
    width: Math.min(
      Math.max(
        baseColumnInfo.width ?? DEFAULT_COLUMN_WIDTH,
        displayName.length * COLUMN_WIDTH_PER_HEADER_CHARACTER
      ),
      MAX_COLUMN_WIDTH
    ),
  };
}

/** @package */
export const dataFrameEditableStyles = StyleSheet.create({
  tableWrapper: {
    height: '100%',
    display: 'inline-block',
    ':hover': {
      background: 'white',
    },
    borderTop: 'none',
    padding: 0,
  },
  typeIcon: {color: GRAY_DARK},
});
